import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { applyTab, sceneById, sceneClock, spaceSceneForTab, stateAt } from "./player.ts";
import { DEFAULT_SCENE_ID, DEMO_SCENES, STORY_SCENE_IDS } from "./scripts.ts";
import type { DemoTarget, ReplicaState, SceneId, SpaceTab } from "./types.ts";
import { CRM_AGENT_ID, MEETING_AGENT_ID } from "./world.ts";

function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function nextStoryScene(sceneId: SceneId): SceneId {
  const index = STORY_SCENE_IDS.indexOf(sceneId as (typeof STORY_SCENE_IDS)[number]);
  if (index < 0) return DEFAULT_SCENE_ID;
  return STORY_SCENE_IDS[(index + 1) % STORY_SCENE_IDS.length] ?? DEFAULT_SCENE_ID;
}

function applyManualActions(
  state: ReplicaState,
  crmApproved: boolean,
  meetingConfirmed: boolean
): ReplicaState {
  let next = state;
  if (crmApproved) {
    next = {
      ...next,
      agents: next.agents.map((agent) =>
        agent.id === CRM_AGENT_ID
          ? { ...agent, status: "present", statusLabel: "已写入 CRM" }
          : agent
      ),
      messages: next.messages.map((message) => {
        if (message.kind !== "answer" || message.card?.kind !== "crm") return message;
        return {
          ...message,
          time: "10:26",
          paragraphs: ["已按批准内容写回关联商机，字段校验与附件关联均成功。"],
          card: { ...message.card, status: "written" }
        };
      })
    };
  }
  if (meetingConfirmed && next.meeting) {
    next = {
      ...next,
      meeting: { ...next.meeting, confirmed: true },
      agents: next.agents.map((agent) =>
        agent.id === MEETING_AGENT_ID
          ? { ...agent, status: "present", statusLabel: "纪要已确认" }
          : agent
      )
    };
  }
  return next;
}

export function useDemoPlayer() {
  const [sceneId, setSceneId] = useState<SceneId>(DEFAULT_SCENE_ID);
  const [elapsed, setElapsed] = useState(0);
  const [paused, setPaused] = useState(false);
  const [autoplay, setAutoplay] = useState(true);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [crmApproved, setCrmApproved] = useState(false);
  const [meetingConfirmed, setMeetingConfirmed] = useState(false);
  const [tabOverride, setTabOverride] = useState<SpaceTab | null>(null);
  const elapsedRef = useRef(0);
  const pausedRef = useRef(false);
  const autoplayRef = useRef(true);
  const sceneIdRef = useRef(sceneId);

  useEffect(() => {
    elapsedRef.current = elapsed;
  }, [elapsed]);

  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);

  useEffect(() => {
    autoplayRef.current = autoplay;
  }, [autoplay]);

  useEffect(() => {
    sceneIdRef.current = sceneId;
  }, [sceneId]);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReducedMotion(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    if (reducedMotion) {
      const finalScene: SceneId = "delivery-dm";
      const scene = sceneById(DEMO_SCENES, finalScene);
      sceneIdRef.current = finalScene;
      elapsedRef.current = scene.duration;
      setSceneId(finalScene);
      setElapsed(scene.duration);
      setAutoplay(false);
      return;
    }

    let frame = 0;
    let last = performance.now();
    let lastPublish = 0;
    const tick = (now: number) => {
      const delta = now - last;
      last = now;
      if (!pausedRef.current && !prefersReducedMotion()) {
        const currentId = sceneIdRef.current;
        const scene = sceneById(DEMO_SCENES, currentId);
        const candidate = elapsedRef.current + delta;
        if (autoplayRef.current && candidate >= scene.duration + scene.hold) {
          const nextId = nextStoryScene(currentId);
          sceneIdRef.current = nextId;
          elapsedRef.current = 0;
          setTabOverride(null);
          setSceneId(nextId);
          setElapsed(0);
        } else {
          const next = autoplayRef.current
            ? Math.min(candidate, scene.duration + scene.hold)
            : sceneClock(scene, candidate, false);
          elapsedRef.current = next;
          if (now - lastPublish >= 48) {
            lastPublish = now;
            setElapsed(next);
          }
        }
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [reducedMotion]);

  const select = useCallback((target: DemoTarget) => {
    if (target.kind === "action") {
      if (target.action === "approve-crm") setCrmApproved(true);
      if (target.action === "confirm-meeting") setMeetingConfirmed(true);
      return;
    }

    autoplayRef.current = false;
    setAutoplay(false);

    if (target.kind === "tab") {
      const nextId = spaceSceneForTab(sceneIdRef.current, target.tab);
      if (nextId !== sceneIdRef.current) {
        const scene = sceneById(DEMO_SCENES, nextId);
        sceneIdRef.current = nextId;
        elapsedRef.current = scene.duration;
        setSceneId(nextId);
        setElapsed(scene.duration);
      }
      setTabOverride(target.tab);
      return;
    }

    const scene = sceneById(DEMO_SCENES, target.sceneId);
    sceneIdRef.current = target.sceneId;
    elapsedRef.current = scene.duration;
    setTabOverride(null);
    setSceneId(target.sceneId);
    setElapsed(scene.duration);
  }, []);

  const state: ReplicaState = useMemo(() => {
    const base = applyManualActions(
      stateAt(sceneById(DEMO_SCENES, sceneId), elapsed, reducedMotion),
      crmApproved,
      meetingConfirmed
    );
    return tabOverride ? applyTab(base, tabOverride) : base;
  }, [crmApproved, elapsed, meetingConfirmed, reducedMotion, sceneId, tabOverride]);

  return {
    state,
    sceneId,
    elapsed,
    paused,
    autoplay,
    reducedMotion,
    select,
    pause: () => setPaused(true),
    resume: () => setPaused(false)
  };
}
